import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import './CashInjection.css';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : (typeof window !== 'undefined' ? window.location.origin : ''));


const CashInjection = ({ onWalletUpdate, countryInfo }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [lastTxn, setLastTxn] = useState(null);
  const [userDetails, setUserDetails] = useState(null);

  // New State for Transaction Mode
  const [mode, setMode] = useState('credit'); // 'credit' (Deposit) or 'debit' (Withdraw)

  const { currentUser } = useAuth();

  const [formData, setFormData] = useState({
    userId: '',
    amount: '',
    currency: 'INR',
    type: 'cash',
    description: '',
    transactionPin: ''
  });

  // Move funds from commission wallet to main wallet
  const [commissionBalance, setCommissionBalance] = useState(0);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferMsg, setTransferMsg] = useState('');

  const fetchCommissionBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/commission-wallet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.success && response.data?.data) {
        setCommissionBalance(response.data.data.balance);
      }
    } catch (e) { /* non-fatal */ }
  };

  const transferToMain = async () => {
    const amount = parseFloat(transferAmount);
    if (!amount || amount <= 0) { setTransferMsg('Enter a valid amount'); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/api/admin/commission/transfer-to-main`,
        { amount }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data?.success) {
        setTransferMsg('Transferred to main wallet');
        setTransferAmount('');
        fetchCommissionBalance();
        if (onWalletUpdate) onWalletUpdate();
      } else {
        setTransferMsg(response.data?.error || response.data?.message || 'Transfer failed');
      }
    } catch (e) {
      setTransferMsg(e.response?.data?.error || e.response?.data?.message || e.message || 'Transfer failed');
    }
  };

  useEffect(() => { fetchCommissionBalance(); }, []);

  // Dynamic currency options based on admin's country
  const getCurrencyOptions = () => {
    const baseOptions = [
      { value: 'INR', label: 'INR (₹)', symbol: '₹' },
      { value: 'USD', label: 'USD ($)', symbol: '$' }
    ];
    
    // Add local currency option if available
    if (countryInfo?.local_currency_code) {
      baseOptions.push({
        value: countryInfo.local_currency_code,
        label: `${countryInfo.local_currency_code} (${countryInfo.local_currency_symbol || countryInfo.local_currency_code})`,
        symbol: countryInfo.local_currency_symbol || countryInfo.local_currency_code
      });
    }
    
    return baseOptions;
  };

  const currencyOptions = getCurrencyOptions();

  const getCurrencySymbol = (currCode) => {
    const code = currCode || formData.currency;
    const found = currencyOptions.find(c => c.value === code);
    return found ? found.symbol : code;
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { 
      headers: { 
        'Content-Type': 'application/json', 
        'Authorization': `Bearer ${token}` 
      } 
    };
  };

  // Fetch user details with wallet balances
  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/admin/users/${userId}/details`,
        getAuthHeader()
      );
      
      if (response.data.success) {
        setUserDetails(response.data.user);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  // Fetch users with wallet balances - FILTERED BY COUNTRY
  const fetchUsers = async (search) => {
    if (!search || search.length < 2) {
      setUsers([]);
      return;
    }

    try {
      setUserSearchLoading(true);
      const response = await axios.get(
        `${API_BASE}/api/admin/users`,
        { 
          ...getAuthHeader(),
          params: { 
            query: search,
            country_id: countryInfo?.country_id // Filter by admin's country
          } 
        }
      );
      
      if (response.data.success) {
        // Filter users by country if we have country_id
        let filteredUsers = response.data.users || [];
        
        // If we have country_id, filter users by same country
        if (countryInfo?.country_id) {
          filteredUsers = filteredUsers.filter(user => 
            user.country_id === countryInfo.country_id
          );
        }
        
        setUsers(filteredUsers);
      }
    } catch (err) { 
      console.error('Search users error:', err);
      setUsers([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setFormData(prev => ({ ...prev, userId: user.id }));
    setUsers([]);
    setSearchTerm('');
    
    // Fetch detailed user information with wallet balances
    fetchUserDetails(user.id);
  };

  // Handle transaction
  const handleTransaction = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setLastTxn(null);

    try {
      // Validate inputs
      if (!formData.userId) {
        throw new Error('Please select a user');
      }

      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        throw new Error('Please enter a valid amount');
      }

      if (!formData.transactionPin || formData.transactionPin.length !== 6) {
        throw new Error('Please enter a valid 6-digit PIN');
      }

      const payload = {
        userId: parseInt(formData.userId),
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        type: formData.type,
        description: formData.description || `${mode === 'credit' ? 'Deposit' : 'Withdrawal'} by Admin`,
        transactionPin: formData.transactionPin,
        action: mode // 'credit' or 'debit'
      };

      const response = await axios.post(
        `${API_BASE}/api/admin/funds/request`,
        payload,
        getAuthHeader()
      );

      if (response.data.success) {
        setSuccess(`${mode === 'credit' ? 'Deposit' : 'Withdrawal'} request submitted for approval.`);

        setLastTxn({
          ref: response.data.request?.id ? `Request #${response.data.request.id}` : 'Pending',
          amount: formData.amount,
          currencySymbol: getCurrencySymbol(formData.currency),
          user: selectedUser?.username || 'User',
          time: new Date().toLocaleString(),
          type: mode === 'credit' ? 'Deposit' : 'Withdrawal',
          status: 'Request Submitted',
          newBalance: 'Pending approval'
        });

        // Reset form (keep selected user + currency)
        setFormData({
          userId: selectedUser?.id || '',
          amount: '',
          currency: formData.currency,
          type: formData.type,
          description: '',
          transactionPin: ''
        });
      }
    } catch (err) {
      console.error('Transaction error:', err);
      setError(err.response?.data?.message || err.message || 'Transaction failed');
    } finally {
      setLoading(false);
    }
  };

  // Quick amount buttons
  const quickAmounts = [100, 500, 1000, 2000, 5000, 10000];

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        fetchUsers(searchTerm);
      } else {
        setUsers([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Get local currency display info
  const localCurrencyCode = countryInfo?.local_currency_code || 'LRD';
  const localCurrencySymbol = countryInfo?.local_currency_symbol || 'L$';

  return (
    <div className="cash-injection">
      <div className="cash-injection-header">
        <div className="cash-injection-header-main">
          <h2>Fund Management</h2>
          <p>Admin Portal / Deposits & Withdrawals</p>
          {countryInfo?.country_name && countryInfo.country_name !== 'Unknown' && (
            <div className="country-filter-info">
              <i className="fas fa-filter"></i>
              Showing users from: <strong>{countryInfo.country_name}</strong>
              <span className="currency-info">
                ({localCurrencyCode}: {localCurrencySymbol})
              </span>
            </div>
          )}
        </div>
        <div className="move-funds-panel">
          <h3>Move funds from commission wallet<br />to main wallet</h3>
          <div className="move-funds-row">
            <span className="commission-balance">Commission: ${commissionBalance.toFixed(2)}</span>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Amount"
              value={transferAmount}
              onChange={(e) => setTransferAmount(e.target.value)}
            />
            <button onClick={transferToMain}>Move</button>
          </div>
          {transferMsg && <span className="transfer-msg">{transferMsg}</span>}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i> {error}
        </div>
      )}
      
      {success && lastTxn && (
        <div className="success-card" style={{borderColor: lastTxn.type === 'Withdrawal' ? '#ef4444' : '#059669'}}>
          <div className="success-header">
             <h3 style={{color: lastTxn.type === 'Withdrawal' ? '#b91c1c' : '#059669'}}>
                <i className={`fas fa-${lastTxn.type === 'Withdrawal' ? 'minus' : 'plus'}-circle`}></i> {lastTxn.type} {lastTxn.status || 'Complete'}
             </h3>
             <button onClick={() => {setSuccess(''); setLastTxn(null);}} className="close-btn">
               <i className="fas fa-times"></i>
             </button>
          </div>
          <div className="txn-grid">
            <div className="txn-item">
              <label>Ref No</label>
              <span className="txn-ref">{lastTxn.ref}</span>
            </div>
            <div className="txn-item">
              <label>Amount</label>
              <span>{lastTxn.currencySymbol} {lastTxn.amount}</span>
            </div>
            <div className="txn-item">
              <label>User</label>
              <span>{lastTxn.user}</span>
            </div>
            <div className="txn-item">
              <label>Status</label>
              <span>{lastTxn.newBalance || 'Pending approval'}</span>
            </div>
            <div className="txn-item">
              <label>Time</label>
              <span>{lastTxn.time}</span>
            </div>
          </div>
        </div>
      )}

      <div className="cash-injection-content">
        
        {/* LEFT: USER SELECTION */}
        <div className="user-selection-section">
          <h3>Step 1: Identify Player</h3>
          
          <div className="form-group">
            <label>Search Player</label>
            <div className="search-input-container">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Name, Email or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {userSearchLoading && (
                  <div className="search-loading">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                )}
            </div>
          </div>
          
          {users.length > 0 && (
            <div className="user-search-results">
              {users.map(u => (
                <div key={u.id} className="user-result-item" onClick={() => handleUserSelect(u)}>
                  <div className="user-info">
                    <strong>{u.username}</strong>
                    <small>{u.email}</small>
                    {u.country_name && (
                      <small className="user-country">
                        <i className="fas fa-map-marker-alt"></i> {u.country_name}
                      </small>
                    )}
                  </div>
                  <div className="user-balances">
                     <span className="mini-badge inr">₹{u.wallet_balance_inr || 0}</span>
                     <span className="mini-badge usd">${u.wallet_balance_usd || 0}</span>
                     <span className="mini-badge local">
                       {localCurrencySymbol}{u.wallet_balance_local || 0}
                     </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedUser ? (
            <div className="selected-user">
              <div className="selected-user-info">
                <div className="user-avatar">{selectedUser.username[0].toUpperCase()}</div>
                <div className="user-details">
                  <h4>{selectedUser.username}</h4>
                  <small>ID: #{selectedUser.id}</small>
                  <small>Email: {selectedUser.email}</small>
                  {selectedUser.country_name && (
                    <small className="user-country-info">
                      <i className="fas fa-globe"></i> {selectedUser.country_name}
                    </small>
                  )}
                </div>
              </div>

              <div className="balance-section">
                <label className="balance-label">CURRENT WALLET BALANCES:</label>
                <div className="balance-grid">
                  <div className="balance-box">
                      <small>INR (₹)</small>
                      <strong>{userDetails?.wallet_balance_inr || selectedUser.wallet_balance_inr || 0}</strong>
                  </div>
                  <div className="balance-box">
                      <small>USD ($)</small>
                      <strong>{userDetails?.wallet_balance_usd || selectedUser.wallet_balance_usd || 0}</strong>
                  </div>
                  <div className="balance-box">
                      <small>{localCurrencyCode} ({localCurrencySymbol})</small>
                      <strong>{userDetails?.wallet_balance_local || selectedUser.wallet_balance_local || 0}</strong>
                  </div>
                </div>
              </div>
              
              <button 
                className="change-user-btn" 
                onClick={() => {
                  setSelectedUser(null);
                  setUserDetails(null);
                  setFormData(prev => ({ ...prev, userId: '' }));
                }}
              >
                CHANGE PLAYER
              </button>
            </div>
          ) : (
            <div className="empty-state">
               <i className="fas fa-user-circle"></i>
               <p>No Player Selected</p>
               <small>
                 {countryInfo?.country_name 
                   ? `Search for users in ${countryInfo.country_name}`
                   : 'Search and select a user to proceed'}
               </small>
            </div>
          )}
        </div>

        {/* RIGHT: TRANSACTION FORM */}
        <div className="injection-form-section" style={{opacity: selectedUser ? 1 : 0.6, pointerEvents: selectedUser ? 'all' : 'none'}}>
          <h3>Step 2: Transaction Details</h3>
          
          <form onSubmit={handleTransaction}>
            
            {/* ACTION TOGGLE */}
            <div className="form-group">
                <label>Action</label>
                <div className="mode-toggle">
                    <button 
                      type="button" 
                      className={`mode-btn deposit ${mode === 'credit' ? 'active' : ''}`}
                      onClick={() => setMode('credit')}
                    >
                      <i className="fas fa-plus-circle"></i> DEPOSIT (ADD)
                    </button>
                    <button 
                      type="button" 
                      className={`mode-btn withdraw ${mode === 'debit' ? 'active' : ''}`}
                      onClick={() => setMode('debit')}
                    >
                      <i className="fas fa-minus-circle"></i> WITHDRAW (DEDUCT)
                    </button>
                </div>
            </div>

            <div className="form-group">
                <label>Currency & Type</label>
                <div className="currency-type-row">
                    <select 
                        name="currency" 
                        value={formData.currency}
                        onChange={(e) => setFormData({...formData, currency: e.target.value})}
                        className="currency-select"
                    >
                        {currencyOptions.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </select>
                    
                    <div className="type-selector">
                        <button 
                          type="button" 
                          className={`type-btn ${formData.type === 'cash' ? 'active' : ''}`}
                          onClick={() => setFormData({...formData, type: 'cash'})}
                        >
                          CASH
                        </button>
                        {mode === 'credit' && (
                            <button 
                              type="button" 
                              className={`type-btn ${formData.type === 'bonus' ? 'active' : ''}`}
                              onClick={() => setFormData({...formData, type: 'bonus'})}
                            >
                              BONUS
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="form-group">
              <label>{mode === 'credit' ? 'Amount to Add' : 'Amount to Deduct'}</label>
              <div className="amount-input-wrapper">
                  <span className="currency-prefix">{getCurrencySymbol()}</span>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                    step="0.01"
                    min="0.01"
                    required
                    className="amount-input"
                  />
              </div>
              
              {/* Quick amount buttons */}
              <div className="quick-amounts">
                {quickAmounts.map(amount => (
                  <button
                    key={amount}
                    type="button"
                    className="quick-amount-btn"
                    onClick={() => setFormData({...formData, amount: amount.toString()})}
                  >
                    {getCurrencySymbol()}{amount}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Add a note about this transaction..."
                  rows="2"
                />
            </div>

            <div className="form-group">
                <label>Admin PIN</label>
                <div className="pin-input-wrapper">
                  <i className="fas fa-lock"></i>
                  <input 
                    type="password" 
                    value={formData.transactionPin}
                    onChange={(e) => setFormData({...formData, transactionPin: e.target.value})}
                    placeholder="6-Digit PIN"
                    maxLength="6"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    required
                  />
                </div>
                {process.env.NODE_ENV === 'development' && <small className="pin-hint">Dev PIN: 123456</small>}
            </div>

            <button 
                type="submit" 
                className={`inject-btn ${mode === 'debit' ? 'btn-danger' : 'btn-success'} ${loading ? 'loading' : ''}`}
                disabled={loading || !formData.amount || !selectedUser}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> PROCESSING...
                </>
              ) : (
                `CONFIRM ${mode === 'credit' ? 'DEPOSIT' : 'WITHDRAWAL'}`
              )}
            </button>
            
            <div className="transaction-info">
              <div className="info-item">
                <i className="fas fa-user-shield"></i>
                <span>Admin: {currentUser?.username || 'Admin'}</span>
              </div>
              <div className="info-item">
                <i className="fas fa-history"></i>
                <span>All transactions are logged for audit</span>
              </div>
              {countryInfo?.country_name && (
                <div className="info-item">
                  <i className="fas fa-globe"></i>
                  <span>Country: {countryInfo.country_name}</span>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CashInjection;